import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = mode === "signup" ? await register({ name, company, email, password }) : await login(email, password);
      nav(u.status === "active" && u.role === "admin" ? "/admin" : "/portal");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="/img/vault.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50 grayscale" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent,#080B11)]" />
        <div className="absolute bottom-16 left-16 max-w-md">
          <p className="eyebrow">Corridor Portal</p>
          <p className="mt-4 font-display text-4xl font-medium leading-tight">The shared desk for MidHq and FinBPM.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-5 pt-24">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <LogoMark size={44} />
          <h1 className="mt-8 font-display text-4xl font-semibold tracking-tight" data-testid="login-title">{mode === "signup" ? "Create account" : "Sign in"}</h1>
          <p className="mt-2 text-sm text-slate-400">{mode === "signup" ? "For FinBPM and referral partners. MidHq approves every new account." : "Admins and FinBPM partners. New accounts need MidHq approval."}</p>
          <button type="button" onClick={google} data-testid="login-google-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-white/15 py-3.5 font-display text-sm font-medium transition-colors hover:border-sky-400 hover:bg-white/5">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
          <div className="space-y-4">
            {mode === "signup" && (
              <>
                <input className="field" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="signup-name-input" />
                <input className="field" placeholder="Company" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="signup-company-input" />
              </>
            )}
            <input className="field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            <input className="field" type="password" placeholder={mode === "signup" ? "Password (8+ characters)" : "Password"} minLength={mode === "signup" ? 8 : undefined}
              autoComplete={mode === "signup" ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          </div>
          {mode === "signin" && (
            <div className="mt-3 text-right"><Link to="/forgot-password" className="text-sm text-slate-300 hover:text-white" data-testid="login-forgot-link">Forgot password?</Link></div>
          )}
          {err && <p className="mt-4 text-sm text-rose-400" data-testid="login-error">{err}</p>}
          <button disabled={busy} className="btn-primary mt-8 w-full justify-center" data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "signup" ? "Create account" : "Enter"}
          </button>
          <p className="mt-6 text-center text-sm text-slate-400">
            {mode === "signup" ? "Already have an account? " : "New partner? "}
            <button type="button" onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setErr(""); }} className="text-white underline underline-offset-4 hover:text-[var(--gold)]" data-testid="login-mode-toggle">
              {mode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </p>
        </form>
      </div>
    </main>
  );
}
